import api from '@/services/api';
import type { PaginatedResponse } from '@/types';
import type {
  Patient,
  PatientSearchResult,
  CreatePatientRequest,
  UpdatePatientRequest,
} from '@/types/patient';

const BASE_PATH = '/patients';

/**
 * Patient management API service.
 *
 * Handles CRUD operations for patient records,
 * including search, pagination, and deactivation.
 */
export const patientService = {
  /**
   * Searches patients by name, CPF, or phone with pagination.
   */
  async searchPatients(
    query: string,
    page: number,
    pageSize: number,
  ): Promise<PaginatedResponse<PatientSearchResult>> {
    const { data } = await api.get<PaginatedResponse<PatientSearchResult>>(
      `${BASE_PATH}/search`,
      {
        params: { query, page, limit: pageSize },
      },
    );
    return data;
  },

  /**
   * Fetches a single patient by ID.
   */
  async getPatient(id: string): Promise<Patient> {
    const { data } = await api.get<Patient>(`${BASE_PATH}/${id}`);
    return data;
  },

  /**
   * Creates a new patient.
   */
  async createPatient(payload: CreatePatientRequest): Promise<Patient> {
    const { data } = await api.post<Patient>(BASE_PATH, payload);
    return data;
  },

  /**
   * Updates an existing patient.
   */
  async updatePatient(id: string, payload: UpdatePatientRequest): Promise<Patient> {
    const { data } = await api.put<Patient>(`${BASE_PATH}/${id}`, payload);
    return data;
  },

  /**
   * Deactivates a patient (soft delete).
   */
  async deactivatePatient(id: string): Promise<Patient> {
    const { data } = await api.put<Patient>(`${BASE_PATH}/${id}/deactivate`);
    return data;
  },

  /**
   * Activates a patient.
   */
  async activatePatient(id: string): Promise<Patient> {
    const { data } = await api.put<Patient>(`${BASE_PATH}/${id}/activate`);
    return data;
  },
};
